import axios from "axios";
import React, { useEffect, useState, useMemo, memo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { putApi } from "../../api/getUser";
import {
  fetchServicesTasks,
  fetchServiceTaskCategories,
} from "../../features/servicesTasks/servicesTasksSlice";
import RowOptions from "../../shared/components/table/components/RowOptions";
import VehicleComponent from "../../shared/components/table/components/VehicleComponent";
import {
  AddFormButton,
  ColumnHeading,
} from "../../shared/components/TableElements";
import DataReactTable from "../DataTable/components/DataReactTable";
import { Link } from "react-router-dom";
import { NATEXT } from "../../shared/constants";
import { useAuth } from "../../contexts/AuthContext";
import { updateNoOfRecords } from "../../features/columns/columnsSlice";
import { sortItems } from "../../shared/helpers";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Desc } from "../../shared/components/form/FormElements";
import { colorSwitchActive } from "../../utils/palette";

// Separate component for vehicle cell to properly use hooks
const VehicleCell = memo(({ row, allVehicles }) => {
  const [currentVehicle, setCurrentVehicle] = useState(null);

  useEffect(() => {
    if (allVehicles?.length > 0) {
      if (row.original.veh_id !== "All") {
        let current = allVehicles.find((v) => v.veh_id === row.original.veh_id);
        setCurrentVehicle(current);
      } else {
        setCurrentVehicle(null);
      }
    }
  }, [allVehicles, row]);

  return (
    <VehicleComponent
      vehicleName={currentVehicle ? currentVehicle?.label : "All"}
      vehicleId={currentVehicle ? currentVehicle?.veh_id : ""}
      vehicleImg={currentVehicle ? currentVehicle?.veh_img : ""}
      orgId={row.original.org_id}
      assetType={currentVehicle ? currentVehicle?.asset_type : 0}
    />
  );
});

const ServicesTasksTable = () => {
  const { t } = useTranslation("common");
  const rowData = useSelector((state) => state.servicesTasks.list);
  const serviceTasksStatus = useSelector((state) => state.servicesTasks.status);
  const location = useLocation();
  const fromCancel = location.state?.fromCancel;
  const dispatch = useDispatch();
  const {
    reload,
    setReload,
    setLoading,
    isClientApiCalled,
    setIsClientApiCalled,
    isLoggedIn,
    isViewOnly,
  } = useAuth();
  const allVehicles = useSelector((state) => state.vehicles.list);

  //Effect to set initial default rows
  useEffect(() => {
    if (isLoggedIn && (!isClientApiCalled || !fromCancel)) {
      (async () => {
        setIsClientApiCalled(true);
        setLoading(true);
        const apiData = {
          querystring: "get_user_screen_settings",
          screen_name: "servicetasks",
        };
        const userSettingsRes = await putApi(apiData, "dashboard");
        console.log("userSettingsRes", userSettingsRes);
        if (userSettingsRes.success) {
          const { data } = userSettingsRes;
          const { default_rows } = data;
          dispatch(updateNoOfRecords({ defaultRows: default_rows }));
          setLoading(false);
        }
      })();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    (async () => {
      if (reload === "servicetasks") {
        setReload("");
        const apiData = {
          querystring: "getallservicetask",
          service_veh_id: "All",
        };
        dispatch(fetchServicesTasks(apiData));
      }
    })();
  }, [reload]);

  // Fetch service task categories on mount
  useEffect(() => {
    dispatch(fetchServiceTaskCategories());
  }, []);

  const columnHeaders = useMemo(
    () => [
      {
        id: 1,
        Header: t("asset"),
        accessor: "veh_id",
        Cell: ({ row }) => <VehicleCell row={row} allVehicles={allVehicles} />,
        //disableGlobalFilter: true,
      },
      {
        id: 2,
        Header: t("service_task_name_caps"),
        accessor: "service_task_name",
        accessorId: "task_name",
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, "service_task_name");
        },
        //disableGlobalFilter: true,
      },
      {
        id: 3,
        Header: t("category"),
        accessor: "category_name",
        accessorId: "task_name",
        Cell: ({ value }) => value || <Desc>{t("uncategorized")}</Desc>,
        sortType: (prev, curr) => {
          const a = prev.original.category_name || "";
          const b = curr.original.category_name || "";
          if (!a && b) return 1;
          if (a && !b) return -1;
          return a.toLowerCase() > b.toLowerCase()
            ? 1
            : a.toLowerCase() < b.toLowerCase()
            ? -1
            : 0;
        },
        //disableGlobalFilter: true,
      },
      {
        id: 4,
        Header: t("recurring"),
        accessor: "recurring",
        Cell: ({ value }) =>
          value ? (
            <i
              className="fi fi-rr-rotate-right"
              style={{ color: colorSwitchActive }}
            ></i>
          ) : (
            NATEXT
          ),
      },
      // {
      //   id: 3,
      //   Header: "Last Service Date",
      //   accessor: "service_date",
      //   Cell: ({ row }) =>
      //     row?.original?.service_date
      //       ? new Date(row?.original?.service_date * 1000).toLocaleDateString(
      //           "en-GB"
      //         )
      //       : NATEXT,
      //   //disableGlobalFilter: true,
      // },
      {
        id: "edit",
        Header: "",
        accessor: "edit",
        accessorId: "edit",
        Cell: ({ row }) => <RowOptions rowData={row.original} />,
        disableSortBy: true,
      },
    ],
    [t, allVehicles],
  );

  const reactTableData = {
    tableHeaderData: columnHeaders,
    tableRowsData: rowData,
  };
  return (
    <Container>
      <Row className="mx-1 d-flex align-items-center justify-content-around">
        <Col
          sm={10}
          //className="d-flex justify-content-start align-items-center"
        >
          <ColumnHeading>{t("service_tasks")}</ColumnHeading>
        </Col>
        <Col
          sm={2}
          className="d-flex justify-content-end align-items-center mb-1"
        >
          {!isViewOnly && (
            <Link to={"/services/servicetasks/addnew"}>
              <AddFormButton>{t("add_service_task")}</AddFormButton>
            </Link>
          )}
        </Col>
      </Row>
      <Row>
        <DataReactTable
          reactTableData={reactTableData}
          //valuesArray={servicesValuesArray}
          //initialData={initialData}
          //filterToggleColumns={filterToggleColumns}
          //defaultColumns={defaultColumns}
          //columnSizeData={columnSizeData}
        />
      </Row>
    </Container>
  );
};

export default ServicesTasksTable;
